/**
 * Wire ↔ domain converters for the Otto API.
 *
 * The backend speaks snake_case JSON. Frontend domain types are camelCase
 * (see `../types/otto-models.ts`). Every wire payload passes through one
 * of these functions before reaching consumer code.
 */

/* eslint-disable @typescript-eslint/naming-convention */
// Wire types intentionally use snake_case to mirror the backend JSON.
// The conversion to camelCase happens inside the converter bodies.

import {CATEGORY_SCOPE_RECORD_ID} from '@/matic/features/relay/lib/scope-utils';
import {
  toViewDefinition,
  toViewSteer,
  type WireViewDefinition,
  type WireViewSteer,
} from '../views/api/view-api';
import {VIEW_DEFINITION_CHANGE_KINDS} from '../views/types';
import type {
  CetecRefEntityKind,
  ChatReference,
  ChatReferenceKind,
  ColumnDefinition,
  ColumnType,
  CorpusItem,
  ErpWriteCard,
  ErpWriteRow,
  LatticeMode,
  LatticeState,
  OttoAnchor,
  OttoHistoryMessage,
  OttoHistoryResult,
  OttoMessageRequest,
  OttoSession,
  OttoSessionContext,
  OttoSessionSummary,
  OttoSharedChat,
  OttoShareResult,
  OttoStreamEvent,
  OttoTaggedRef,
  OttoToolCallSummary,
  PageType,
  PlanCheckpointBlock,
  RowDefinition,
  SessionStatus,
  UserPrompt,
  UserPromptFlag,
  UserPromptOption,
  UserPromptQuote,
  ErpCitationEvidence,
} from '../types/otto-models';

// ── Inbound: wire → domain ───────────────────────────────────────────

export type WireAnchor = {
  type_id: string;
  // Null = a Category anchor (the whole ObjectType, "All Quotes") — mirrors
  // the backend's MdmAnchorRef.record_id. OttoAnchor keeps the '*' sentinel
  // (CATEGORY_SCOPE_RECORD_ID, same spelling the Library scope param uses)
  // on the domain side; only the wire boundary converts between the two —
  // see `toAnchor` / `fromAnchor` below.
  record_id: string | null;
  // Display fields are optional — older sessions won't have them.
  // Sent in the PATCH so the backend stores them in its JSONB context blob
  // and echoes them back on GET, preserving labels across page reloads.
  display_name?: string | null;
  type_name?: string | null;
};

export type WireTaggedRef = {
  kind: 'document' | 'record' | 'thread' | 'file' | 'cetec';
  id: string;
  display_label?: string | null;
  // Cetec tags carry their full chip payload — there is no lazy lookup for
  // ERP refs, so the label + deep link round-trip through the JSONB context
  // blob like display_label does.
  entity_kind?: string | null;
  identifier?: string | null;
  name?: string | null;
  url?: string | null;
};

export type WireSessionContext = {
  anchor_refs?: WireAnchor[] | null;
  tagged_refs?: WireTaggedRef[] | null;
};

export type WireSession = {
  id: string;
  page_type: string;
  status: string;
  entity_id?: string | null;
  entity_display_name?: string | null;
  title?: string | null;
  created_at: string;
  updated_at: string;
  last_message_at?: string | null;
  pinned_at?: string | null;
  context?: WireSessionContext | null;
};

function toAnchor(wire: WireAnchor): OttoAnchor {
  return {
    typeId: wire.type_id,
    recordId: wire.record_id ?? CATEGORY_SCOPE_RECORD_ID,
    ...(wire.display_name ? {displayName: wire.display_name} : {}),
    ...(wire.type_name ? {typeName: wire.type_name} : {}),
  };
}

const CETEC_ENTITY_KINDS: readonly CetecRefEntityKind[] = [
  'part',
  'order',
  'po',
  'customer',
];

function toCetecEntityKind(value: unknown): CetecRefEntityKind | undefined {
  return CETEC_ENTITY_KINDS.find((kind) => kind === value);
}

/** Outbound counterpart of `toAnchor` — the '*' sentinel becomes `null`. */
export function fromAnchorRecordId(recordId: string): string | null {
  return recordId === CATEGORY_SCOPE_RECORD_ID ? null : recordId;
}

function toTaggedRef(wire: WireTaggedRef): OttoTaggedRef {
  const entityKind = toCetecEntityKind(wire.entity_kind);
  return {
    kind: wire.kind,
    id: wire.id,
    ...(wire.display_label ? {displayLabel: wire.display_label} : {}),
    ...(entityKind ? {entityKind} : {}),
    ...(wire.identifier ? {identifier: wire.identifier} : {}),
    ...(wire.name ? {name: wire.name} : {}),
    ...(wire.url ? {url: wire.url} : {}),
  };
}

export function toOttoSessionContext(
  wire: WireSessionContext | null | undefined
): OttoSessionContext {
  return {
    anchorRefs: (wire?.anchor_refs ?? []).map((r) => toAnchor(r)),
    taggedRefs: (wire?.tagged_refs ?? []).map((r) => toTaggedRef(r)),
  };
}

export function toOttoSession(wire: WireSession): OttoSession {
  return {
    id: wire.id,
    pageType: wire.page_type as PageType,
    status: wire.status as SessionStatus,
    ...(wire.entity_id ? {entityId: wire.entity_id} : {}),
    ...(wire.entity_display_name
      ? {entityDisplayName: wire.entity_display_name}
      : {}),
    ...(wire.title ? {title: wire.title} : {}),
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
    ...(wire.last_message_at ? {lastMessageAt: wire.last_message_at} : {}),
    ...(wire.pinned_at ? {pinnedAt: wire.pinned_at} : {}),
    ...(wire.context ? {context: toOttoSessionContext(wire.context)} : {}),
  };
}

export type WireSessionSummary = {
  id: string;
  page_type: string;
  status: string;
  entity_id?: string | null;
  entity_display_name?: string | null;
  title?: string | null;
  last_message_at?: string | null;
  pinned_at?: string | null;
  context?: WireSessionContext | null;
};

export function toOttoSessionSummary(
  wire: WireSessionSummary
): OttoSessionSummary {
  return {
    id: wire.id,
    pageType: wire.page_type as PageType,
    status: wire.status as SessionStatus,
    ...(wire.entity_id ? {entityId: wire.entity_id} : {}),
    ...(wire.entity_display_name
      ? {entityDisplayName: wire.entity_display_name}
      : {}),
    ...(wire.title ? {title: wire.title} : {}),
    ...(wire.last_message_at ? {lastMessageAt: wire.last_message_at} : {}),
    ...(wire.pinned_at ? {pinnedAt: wire.pinned_at} : {}),
    ...(wire.context ? {context: toOttoSessionContext(wire.context)} : {}),
  };
}

type WireToolCall = {
  name: string;
  input?: Record<string, unknown>;
};

type WireHistoryMessage = {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string | null;
  tool_calls?: WireToolCall[];
  references?: Array<Record<string, unknown>>;

  user_prompt?: Record<string, unknown> | null;
  erp_writes?: Array<Record<string, unknown>>;
};

/**
 * Wire → domain for an `ask_user` question. Returns `null` when the payload
 * is missing a question or has fewer than one usable option (defensive: the
 * backend guarantees 2-4, but rehydrated legacy/corrupt rows shouldn't crash
 * the transcript). Shared by the history mapper and the SSE mapper.
 */
function toAccessoryAction(raw: unknown): UserPromptOption['accessory'] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  if (
    typeof record.pn !== 'string' ||
    !record.pn ||
    typeof record.line_id !== 'string' ||
    !record.line_id ||
    typeof record.revision !== 'string' ||
    !record.revision
  )
    return undefined;
  if (
    record.qty !== undefined &&
    (!Number.isSafeInteger(record.qty) || Number(record.qty) < 1)
  )
    return undefined;
  return {
    pn: record.pn,
    lineId: record.line_id,
    revision: record.revision,
    ...(record.qty === undefined ? {} : {quantity: Number(record.qty)}),
  };
}

export function toUserPrompt(
  raw: Record<string, unknown> | null | undefined
): UserPrompt | null {
  if (!raw || typeof raw !== 'object') return null;
  const question = typeof raw.question === 'string' ? raw.question : '';
  if (!question) return null;
  const rawOptions = Array.isArray(raw.options) ? raw.options : [];
  const options: UserPromptOption[] = [];
  for (const item of rawOptions) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const label = typeof record.label === 'string' ? record.label : '';
    if (!label) continue;
    const action = toAccessoryAction(record.accessory);
    options.push({
      label,
      ...(action ? {accessory: action} : {}),
      ...(typeof record.description === 'string' && record.description
        ? {description: record.description}
        : {}),
      ...(typeof record.value === 'string' && record.value
        ? {value: record.value}
        : {}),
      ...(Array.isArray(record.flags)
        ? {flags: toUserPromptFlags(record.flags)}
        : {}),
    });
  }

  if (options.length === 0) return null;
  const quote = toUserPromptQuote(raw.quote);
  return {
    question,
    options,
    allowMultiple: Boolean(raw.allow_multiple),
    allowFreeform:
      raw.allow_freeform === undefined ? true : Boolean(raw.allow_freeform),
    ...(quote ? {quote} : {}),
  };
}

/** A flag-choice option's flags; malformed entries are dropped. */
function toUserPromptFlags(raw: unknown[]): UserPromptFlag[] {
  const flags: UserPromptFlag[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const {field, note} = item as Record<string, unknown>;
    if (typeof field === 'string' && field && typeof note === 'string' && note)
      flags.push({field, note});
  }

  return flags;
}

/** The quote binding on a `quote_present_choice` prompt; null for a plain `ask_user`. */
function toUserPromptQuote(raw: unknown): UserPromptQuote | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const requestId =
    typeof record.request_id === 'string' ? record.request_id : '';
  const asOf = typeof record.as_of === 'string' ? record.as_of : '';
  const stage = typeof record.stage === 'string' ? record.stage : '';
  if (!requestId || !asOf || !stage) return null;
  const text = (key: string): string | undefined =>
    typeof record[key] === 'string' && record[key] ? record[key] : undefined;
  return {
    requestId,
    asOf,
    stage,
    ...(record.kind === 'flag' ||
    record.kind === 'accessory' ||
    record.kind === 'read_review'
      ? {kind: record.kind}
      : {}),
    ...(text('card') ? {card: text('card')} : {}),
    ...(text('field') ? {field: text('field')} : {}),
    ...(text('recommended') ? {recommended: text('recommended')} : {}),
    ...(text('review_id') ? {reviewId: text('review_id')} : {}),
    ...(text('preview_token') ? {previewToken: text('preview_token')} : {}),
  };
}

export type WireHistoryResponse = {
  session_id: string;
  messages: WireHistoryMessage[];
};

/**
 * Cetec write ledger card. Drops a payload with no rows at all rather than
 * rendering an empty card — a card that says nothing is worse than none.
 */
export function toErpWriteCard(
  raw: Record<string, unknown> | null | undefined
): ErpWriteCard | null {
  if (!raw || typeof raw !== 'object') return null;
  const objectRecordId = asString(raw.object_record_id);
  if (!objectRecordId) return null;

  const rows = (value: unknown): ErpWriteRow[] => {
    if (!Array.isArray(value)) return [];
    const out: ErpWriteRow[] = [];
    for (const item of value) {
      if (!item || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const fieldKey = asString(record.field_key);
      if (!fieldKey) continue;
      out.push({
        fieldKey,
        label: asString(record.label) || fieldKey,
        currentValue: record.current_value,
        proposedValue: record.proposed_value,
      });
    }

    return out;
  };

  const written = rows(raw.written);
  const pending = rows(raw.pending);
  const conflicts = rows(raw.conflicts);
  const unchanged = rows(raw.unchanged);
  if (
    written.length === 0 &&
    pending.length === 0 &&
    conflicts.length === 0 &&
    unchanged.length === 0
  ) {
    return null;
  }

  const writeError = asString(raw.write_error);
  // Not yet emitted by the backend — see the `objectTypeId?` doc comment on
  // `ErpWriteCard`. Read defensively so this activates the moment it lands.
  const objectTypeId = asString(raw.object_type_id);
  return {
    objectRecordId,
    ...(objectTypeId ? {objectTypeId} : {}),
    entityKind: asString(raw.entity_kind),
    externalId: asString(raw.external_id),
    written,
    pending,
    conflicts,
    unchanged,
    ...(writeError ? {writeError} : {}),
  };
}

function toToolCallSummary(wire: WireToolCall): OttoToolCallSummary {
  return {name: wire.name, input: wire.input ?? {}};
}

function toHistoryMessage(wire: WireHistoryMessage): OttoHistoryMessage {
  const references = (wire.references ?? []).map((r) => toChatReference(r));
  const userPrompt = toUserPrompt(wire.user_prompt);
  const erpWrites = (wire.erp_writes ?? [])
    .map((raw) => toErpWriteCard(raw))
    .filter((card): card is ErpWriteCard => card !== null);
  return {
    role: wire.role,
    content: wire.content,
    ...(wire.timestamp ? {timestamp: wire.timestamp} : {}),
    toolCalls: (wire.tool_calls ?? []).map((tc) => toToolCallSummary(tc)),
    ...(references.length > 0 ? {references} : {}),
    ...(userPrompt ? {userPrompt} : {}),
    ...(erpWrites.length > 0 ? {erpWrites} : {}),
  };
}

export function toOttoHistoryResult(
  wire: WireHistoryResponse
): OttoHistoryResult {
  return {
    sessionId: wire.session_id,
    messages: wire.messages.map((m) => toHistoryMessage(m)),
  };
}

export type WireShareResponse = {
  share_id: string;
  url: string;
};

export function toOttoShareResult(wire: WireShareResponse): OttoShareResult {
  return {shareId: wire.share_id, url: wire.url};
}

export type WireSharedChat = {
  share_id: string;
  title?: string | null;
  owner_id: string;
  anchor?: Array<Record<string, unknown>> | null;
  created_at: string;
  updated_at: string;
  messages: WireHistoryMessage[];
};

export function toOttoSharedChat(wire: WireSharedChat): OttoSharedChat {
  return {
    shareId: wire.share_id,
    ...(wire.title ? {title: wire.title} : {}),
    ownerId: wire.owner_id,
    ...(wire.anchor ? {anchor: wire.anchor} : {}),
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
    messages: wire.messages.map((m) => toHistoryMessage(m)),
  };
}

// ── Inbound: SSE event payloads ──────────────────────────────────────

type WireColumn = {
  label: string;
  column_type: string;
  id?: string | null;
  prompt?: string | null;
  metadata_field?: string | null;
  record_type_id?: string | null;
  record_field_key?: string | null;
};

function toColumn(wire: WireColumn): ColumnDefinition {
  return {
    label: wire.label,
    columnType: wire.column_type as ColumnType,
    ...(wire.id ? {id: wire.id} : {}),
    ...(wire.prompt ? {prompt: wire.prompt} : {}),
    ...(wire.metadata_field ? {metadataField: wire.metadata_field} : {}),
    ...(wire.record_type_id ? {recordTypeId: wire.record_type_id} : {}),
    ...(wire.record_field_key ? {recordFieldKey: wire.record_field_key} : {}),
  };
}

type WireRow = {
  document_id?: string | null;
  record_id?: string | null;
  criterion_text?: string | null;
  id?: string | null;
};

function toRow(wire: WireRow): RowDefinition {
  return {
    ...(wire.document_id ? {documentId: wire.document_id} : {}),
    ...(wire.record_id ? {recordId: wire.record_id} : {}),
    ...(wire.criterion_text ? {criterionText: wire.criterion_text} : {}),
    ...(wire.id ? {id: wire.id} : {}),
  };
}

type WireCorpusItem = {
  document_id?: string | null;
  record_id?: string | null;
};

function toCorpusItem(wire: WireCorpusItem): CorpusItem {
  return {
    ...(wire.document_id ? {documentId: wire.document_id} : {}),
    ...(wire.record_id ? {recordId: wire.record_id} : {}),
  };
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

/**
 * Cetec citation fields — the ERP entity kind, identifier, and deep link
 * (`url` — distinct from `web_url`, which Teams-message refs use).
 */
function toCetecReferenceFields(
  data: Record<string, unknown>
): Partial<ChatReference> {
  const entityKind = toCetecEntityKind(data.entity_kind);
  return {
    ...(entityKind ? {entityKind} : {}),
    ...(typeof data.identifier === 'string'
      ? {identifier: data.identifier}
      : {}),
    ...(typeof data.url === 'string' ? {url: data.url} : {}),
  };
}

function toMdmDestination(
  value: unknown
): ErpCitationEvidence['mdmDestination'] {
  if (!value || typeof value !== 'object') return undefined;
  const destination = value as Record<string, unknown>;
  if (
    typeof destination.type_id !== 'string' ||
    typeof destination.record_id !== 'string'
  )
    return undefined;
  return {
    typeId: destination.type_id,
    recordId: destination.record_id,
    ...(typeof destination.display_id === 'string'
      ? {displayId: destination.display_id}
      : {}),
    ...(typeof destination.name === 'string' ? {name: destination.name} : {}),
  };
}

function toErpEvidence(value: unknown): ErpCitationEvidence | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const data = value as Record<string, unknown>;
  if (
    typeof data.source_system !== 'string' ||
    typeof data.entity_kind !== 'string' ||
    typeof data.identifier !== 'string' ||
    typeof data.title !== 'string' ||
    typeof data.captured_at !== 'string' ||
    (data.source_state !== 'live' &&
      data.source_state !== 'cached' &&
      data.source_state !== 'stale_cache') ||
    !data.summary ||
    typeof data.summary !== 'object' ||
    !Array.isArray(data.sections)
  )
    return undefined;
  const sections = data.sections.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const section = item as Record<string, unknown>;
    if (typeof section.title !== 'string' || !Array.isArray(section.rows))
      return [];
    return [
      {
        title: section.title,
        ...(section.summary && typeof section.summary === 'object'
          ? {summary: section.summary as Record<string, unknown>}
          : {}),
        rows: section.rows.filter(
          (row): row is Record<string, unknown> =>
            Boolean(row) && typeof row === 'object'
        ),
      },
    ];
  });
  const mdmDestination = toMdmDestination(data.mdm_destination);
  return {
    sourceSystem: data.source_system,
    entityKind: data.entity_kind,
    identifier: data.identifier,
    title: data.title,
    capturedAt: data.captured_at,
    sourceState: data.source_state,
    ...(typeof data.fetched_at === 'string'
      ? {fetchedAt: data.fetched_at}
      : {}),
    ...(typeof data.warning === 'string' ? {warning: data.warning} : {}),
    summary: data.summary as Record<string, unknown>,
    sections,
    ...(mdmDestination ? {mdmDestination} : {}),
  };
}

/** Validated wire-enum cast — an unrecognized value falls back rather than
 * smuggling an arbitrary backend string into a narrow union type. */
function asOneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  return typeof value === 'string' &&
    (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function toChatReference(data: Record<string, unknown>): ChatReference {
  const ref = asString(data.ref);
  const kind = asString(data.kind) as ChatReferenceKind;
  const pages = Array.isArray(data.page_numbers)
    ? (data.page_numbers as unknown[]).filter(
        (n): n is number => typeof n === 'number'
      )
    : undefined;
  return {
    ref,
    kind,
    ...(typeof data.display_id === 'string'
      ? {displayId: data.display_id}
      : {}),
    ...(typeof data.name === 'string' ? {name: data.name} : {}),
    ...(typeof data.snippet === 'string' ? {snippet: data.snippet} : {}),
    ...(pages && pages.length > 0 ? {pageNumbers: pages} : {}),
    ...(typeof data.total_pages === 'number'
      ? {totalPages: data.total_pages}
      : {}),
    ...(typeof data.document_id === 'string'
      ? {documentId: data.document_id}
      : {}),
    ...(typeof data.document_revision_id === 'string'
      ? {documentRevisionId: data.document_revision_id}
      : {}),
    // `file_id` is the single canonical key for a file ref's id — every
    // backend producer emits it (see chat_tools._file_ref).
    ...(typeof data.file_id === 'string' ? {fileId: data.file_id} : {}),
    ...(typeof data.thread_id === 'string' ? {threadId: data.thread_id} : {}),
    ...(typeof data.message_count === 'number'
      ? {messageCount: data.message_count}
      : {}),
    ...(typeof data.teams_message_count === 'number'
      ? {teamsMessageCount: data.teams_message_count}
      : {}),
    ...(typeof data.sender === 'string' ? {sender: data.sender} : {}),
    ...(typeof data.author === 'string' ? {author: data.author} : {}),
    // Teams message citations render standalone from the ref payload.
    ...(data.surface_kind === 'chat' || data.surface_kind === 'channel'
      ? {surfaceKind: data.surface_kind}
      : {}),
    ...(typeof data.sent_at === 'string' ? {sentAt: data.sent_at} : {}),
    ...(typeof data.web_url === 'string' ? {webUrl: data.web_url} : {}),
    ...(typeof data.body === 'string' ? {body: data.body} : {}),
    ...(typeof data.message_id === 'string'
      ? {messageId: data.message_id}
      : {}),
    ...(typeof data.note_id === 'string' ? {noteId: data.note_id} : {}),
    ...(typeof data.type_id === 'string' ? {typeId: data.type_id} : {}),
    ...(typeof data.record_id === 'string' ? {recordId: data.record_id} : {}),
    ...toCetecReferenceFields(data),
    ...(toErpEvidence(data.erp_evidence)
      ? {erpEvidence: toErpEvidence(data.erp_evidence)}
      : {}),
  };
}

/**
 * Narrow a parsed SSE event (raw `{type, data}` from the wire) into the
 * `OttoStreamEvent` discriminated union. Returns `null` for unknown
 * types — callers should silently skip those (forward-compatible with
 * future event types added by the backend).
 */
export function toOttoStreamEvent(wire: {
  type: string;
  data: unknown;
}): OttoStreamEvent | null {
  const data = (wire.data ?? {}) as Record<string, unknown>;

  switch (wire.type) {
    case 'token': {
      return {type: 'token', data: {content: asString(data.content)}};
    }

    case 'tool_call': {
      const input = (data.input as Record<string, unknown>) ?? {};
      const toolDetail =
        typeof data.tool_detail === 'string' ? data.tool_detail : null;
      return {
        type: 'toolCall',
        data: {name: asString(data.name), input, tool_detail: toolDetail},
      };
    }

    case 'column_add': {
      return {
        type: 'columnAdd',
        data: {column: toColumn(data.column as WireColumn)},
      };
    }

    case 'row_add': {
      return {
        type: 'rowAdd',
        data: {row: toRow(data.row as WireRow)},
      };
    }

    case 'corpus_add': {
      return {
        type: 'corpusAdd',
        data: {item: toCorpusItem(data.item as WireCorpusItem)},
      };
    }

    case 'column_update': {
      return {
        type: 'columnUpdate',
        data: {
          columnId: asString(data.column_id),
          fields: (data.fields as Record<string, unknown>) ?? {},
        },
      };
    }

    case 'lattice_created': {
      return {
        type: 'latticeCreated',
        data: {latticeId: asString(data.lattice_id)},
      };
    }

    case 'record_created': {
      return {
        type: 'recordCreated',
        data: {
          recordId: asString(data.record_id),
          displayId: asString(data.display_id),
          name: asString(data.name),
        },
      };
    }

    case 'field_write': {
      return {
        type: 'fieldWrite',
        data: {
          recordId: asString(data.record_id),
          fieldKey: asString(data.field_key),
          value: data.value,
        },
      };
    }

    case 'object_records_changed': {
      return {
        type: 'objectRecordsChanged',
        data: {objectTypeId: asString(data.object_type_id)},
      };
    }

    case 'object_types_changed': {
      return {type: 'objectTypesChanged', data: {}};
    }

    case 'attachments_changed': {
      return {type: 'attachmentsChanged', data: {}};
    }

    case 'source_rule_add': {
      const rule = (data.rule ?? {}) as Record<string, unknown>;
      const ruleType = asString(rule.rule_type);
      if (ruleType !== 'document_type' && ruleType !== 'record_type') {
        return null;
      }

      return {
        type: 'sourceRuleAdd',
        data: {
          rule: {
            ruleType,
            sourceTypeId: asString(rule.source_type_id),
            displayName: asString(rule.display_name),
          },
        },
      };
    }

    case 'source_rule_remove': {
      return {
        type: 'sourceRuleRemove',
        data: {ruleId: asString(data.rule_id)},
      };
    }

    case 'row_remove': {
      return {
        type: 'rowRemove',
        data: {rowId: asString(data.row_id)},
      };
    }

    case 'column_remove': {
      return {
        type: 'columnRemove',
        data: {columnId: asString(data.column_id)},
      };
    }

    case 'references': {
      const raw = (data.references as unknown[]) ?? [];
      const refs: ChatReference[] = [];
      for (const item of raw) {
        if (item && typeof item === 'object') {
          refs.push(toChatReference(item as Record<string, unknown>));
        }
      }

      return {type: 'references', data: {references: refs}};
    }

    case 'thread_associated': {
      return {
        type: 'threadAssociated',
        data: {threadId: asString(data.thread_id)},
      };
    }

    case 'file_associated': {
      return {
        type: 'fileAssociated',
        data: {fileId: asString(data.file_id)},
      };
    }

    case 'file_updated': {
      return {
        type: 'fileUpdated',
        data: {fileId: asString(data.file_id)},
      };
    }

    case 'required_associations_update': {
      const types = Array.isArray(data.required_association_types)
        ? (data.required_association_types as unknown[]).map((t) =>
            asString(t as string)
          )
        : [];
      return {
        type: 'requiredAssociationsUpdate',
        data: {requiredAssociationTypes: types, cleared: Boolean(data.cleared)},
      };
    }

    case 'org_knowledge_update': {
      return {
        type: 'orgKnowledgeUpdate',
        data: {
          persistedText:
            typeof data.persisted_text === 'string'
              ? data.persisted_text
              : null,
          cleared: Boolean(data.cleared),
        },
      };
    }

    case 'outlook_allowlist_update': {
      return {
        type: 'outlookAllowlistUpdate',
        data: {
          action: asString(data.action),
          entryType: asString(data.entry_type),
          value: asString(data.value),
        },
      };
    }

    case 'view_definition_updated': {
      // The event carries the FULL updated Definition (source of truth for
      // the FE cache write) + a change descriptor whose block ids drive the
      // canvas flash only.
      const definition = data.definition as WireViewDefinition | undefined;
      if (!definition) return null;
      const change = (data.change ?? {}) as Record<string, unknown>;
      const blockIds = Array.isArray(change.block_ids)
        ? (change.block_ids as unknown[]).filter(
            (id): id is string => typeof id === 'string'
          )
        : [];
      return {
        type: 'viewDefinitionUpdated',
        data: {
          definition: toViewDefinition(definition),
          change: {
            kind: asOneOf(change.kind, VIEW_DEFINITION_CHANGE_KINDS, 'update'),
            blockIds,
          },
        },
      };
    }

    case 'view_steer_updated': {
      // Dedicated steer event (not the definition-shaped one): the payload
      // matches ViewSteer's wire shape so the FE cache-writes it directly,
      // mirroring the REST steer mutation's onSuccess. Drop a payload with no
      // definition id (nothing to key the cache write off).
      const steerWire = data as unknown as WireViewSteer;
      if (!steerWire.definition_id || !steerWire.scope_key) return null;
      return {
        type: 'viewSteerUpdated',
        data: {steer: toViewSteer(steerWire)},
      };
    }

    case 'plan_checkpoint': {
      // Otto's proposed build plan for a new View — surfaced on the canvas as
      // a confirmation card. Ephemeral (not persisted). Drop a malformed
      // payload (no view name) rather than rendering an empty plan card.
      const viewName = asString(data.view_name);
      if (!viewName) return null;
      const rawBlocks = Array.isArray(data.blocks) ? data.blocks : [];
      const blocks: PlanCheckpointBlock[] = [];
      for (const item of rawBlocks) {
        if (!item || typeof item !== 'object') continue;
        const record = item as Record<string, unknown>;
        blocks.push({
          blockType: asString(record.block_type),
          purpose: asString(record.purpose),
        });
      }

      return {
        type: 'planCheckpoint',
        data: {
          checkpoint: {
            viewName,
            blocks,
            ...(typeof data.summary === 'string' && data.summary
              ? {summary: data.summary}
              : {}),
          },
        },
      };
    }

    case 'user_prompt': {
      // Ask_user question — renders option cards under the assistant turn.
      // Drop malformed payloads (missing question / no usable option) rather
      // than surfacing an empty card set.
      const prompt = toUserPrompt(data);
      if (!prompt) return null;
      return {type: 'userPrompt', data: {prompt}};
    }

    case 'erp_write': {
      // Cetec write ledger — renders the card under the assistant turn.
      // Dropped when it carries no rows, same reasoning as user_prompt.
      const card = toErpWriteCard(data);
      if (!card) return null;
      return {type: 'erpWrite', data: {card}};
    }

    case 'view_builder_handoff': {
      // Library-chat → View-builder handoff (open_view_builder). Ephemeral
      // like plan_checkpoint — forwarded to a callback, never stamped onto
      // the message, so it can't re-fire from history rehydration. Drop a
      // malformed payload (no brief) rather than opening an empty builder.
      const brief = asString(data.brief);
      if (!brief) return null;
      return {type: 'viewBuilderHandoff', data: {brief}};
    }

    case 'anchor_suggestion': {
      return {
        type: 'anchorSuggestion',
        data: {
          typeId: asString(data.type_id),
          recordId: asString(data.record_id),
          displayName: asString(data.display_name),
          ...(typeof data.type_name === 'string'
            ? {typeName: data.type_name}
            : {}),
        },
      };
    }

    case 'done': {
      const {content} = data;
      return {
        type: 'done',
        data: typeof content === 'string' ? {content} : {},
      };
    }

    case 'error': {
      return {
        type: 'error',
        data: {message: asString(data.message, 'Otto encountered an error.')},
      };
    }

    default: {
      // Forward-compat: unknown server events are dropped silently in prod
      // so an additive backend deploy doesn't crash older clients. In dev we
      // surface the gap so engineers notice when a new event needs wiring.
      if (import.meta.env.DEV) {
        console.warn(`[Otto] Unknown SSE event type: ${wire.type}`);
      }

      return null;
    }
  }
}

// ── Outbound: domain → wire ──────────────────────────────────────────

type WireColumnRequest = {
  label: string;
  column_type: string;
  id?: string;
  prompt?: string;
  metadata_field?: string;
  record_type_id?: string;
  record_field_key?: string;
};

type WireRowRequest = {
  document_id?: string;
  record_id?: string;
  criterion_text?: string;
  id?: string;
};

type WireCorpusItemRequest = {
  document_id?: string;
  record_id?: string;
};

type WireSourceRuleRequest = {
  id: string;
  rule_type: 'document_type' | 'record_type';
  source_type_id: string;
  display_name: string;
};

type WireLatticeStateRequest = {
  name: string;
  mode: LatticeMode;
  description?: string;
  columns: WireColumnRequest[];
  rows: WireRowRequest[];
  corpus_items: WireCorpusItemRequest[];
  source_rules?: WireSourceRuleRequest[];
  row_count: number;
};

type WireMessageRequest = {
  message: string;
  images?: Array<{format: string; data: string}>;
  surface: 'list' | 'detail' | 'default' | 'triage';
  lattice_state?: WireLatticeStateRequest;
  lattice_id?: string;
  lattice_name?: string;
  active_section?: string;
  active_record_id?: string;
  view_definition_id?: string;
  view_scope_mode?: 'records' | 'type';
  aggregate_type_id?: string;
  include_view_content?: boolean;
  quote_context?: {
    request_id?: string;
    as_of: string;
    lane: 'open' | 'graded';
    stage?: string;
    page?: 'home' | 'management' | 'detail';
    view?: 'request' | 'quote' | 'evidence';
    visible_request_ids?: string[];
    visible_quote_count?: number;
    visible_intake_count?: number;
    filters?: {
      search?: string;
      statuses?: string[];
      owners?: string[];
      kinds?: string[];
    };
  };
  procurement_context?: {
    object_type_id: string;
    object_type_name: string;
    roots: string[];
    build_qty: number;
    mode: 'flat' | 'tree';
    short_only: boolean;
    search: string;
    rendered_count: number;
    unknown_count: number;
    last_synced_at?: string;
  };
  create_session?: boolean;
  context?: {
    anchor_refs: Array<{type_id: string; record_id: string | null}>;
    tagged_refs: Array<{kind: string; id: string}>;
    title?: string;
  };
  fork_from_share_id?: string;
  page_type?: 'relay' | 'view_builder' | 'procurement' | 'quote';
};

export type WireSessionContextUpdateRequest = {
  anchor_refs?: WireAnchor[] | null;
  tagged_refs?: WireTaggedRef[] | null;
};

export type SessionContextUpdate = {
  /** Undefined = leave unchanged; [] = clear all; non-empty = replace */
  anchorRefs?: OttoAnchor[];
  taggedRefs?: OttoTaggedRef[];
};

export function toWireSessionContextUpdate(
  update: SessionContextUpdate
): WireSessionContextUpdateRequest {
  const wire: WireSessionContextUpdateRequest = {};

  if (update.anchorRefs !== undefined) {
    wire.anchor_refs = update.anchorRefs.map((a) => ({
      type_id: a.typeId,
      record_id: fromAnchorRecordId(a.recordId),
      ...(a.displayName ? {display_name: a.displayName} : {}),
      ...(a.typeName ? {type_name: a.typeName} : {}),
    }));
  }

  if (update.taggedRefs !== undefined) {
    wire.tagged_refs = update.taggedRefs.map((r) => ({
      kind: r.kind,
      id: r.id,
      ...(r.displayLabel ? {display_label: r.displayLabel} : {}),
      ...(r.entityKind ? {entity_kind: r.entityKind} : {}),
      ...(r.identifier ? {identifier: r.identifier} : {}),
      ...(r.name ? {name: r.name} : {}),
      ...(r.url ? {url: r.url} : {}),
    }));
  }

  return wire;
}

function fromColumn(col: ColumnDefinition): WireColumnRequest {
  return {
    label: col.label,
    column_type: col.columnType,
    ...(col.id === undefined ? {} : {id: col.id}),
    ...(col.prompt === undefined ? {} : {prompt: col.prompt}),
    ...(col.metadataField === undefined
      ? {}
      : {metadata_field: col.metadataField}),
    ...(col.recordTypeId === undefined
      ? {}
      : {record_type_id: col.recordTypeId}),
    ...(col.recordFieldKey === undefined
      ? {}
      : {record_field_key: col.recordFieldKey}),
  };
}

function fromRow(row: RowDefinition): WireRowRequest {
  return {
    ...(row.documentId === undefined ? {} : {document_id: row.documentId}),
    ...(row.recordId === undefined ? {} : {record_id: row.recordId}),
    ...(row.criterionText === undefined
      ? {}
      : {criterion_text: row.criterionText}),
    ...(row.id === undefined ? {} : {id: row.id}),
  };
}

function fromCorpusItem(item: CorpusItem): WireCorpusItemRequest {
  return {
    ...(item.documentId === undefined ? {} : {document_id: item.documentId}),
    ...(item.recordId === undefined ? {} : {record_id: item.recordId}),
  };
}

export function toWireLatticeState(
  state: LatticeState
): WireLatticeStateRequest {
  return {
    name: state.name,
    mode: state.mode,
    ...(state.description === undefined
      ? {}
      : {description: state.description}),
    columns: state.columns.map((c) => fromColumn(c)),
    rows: state.rows.map((r) => fromRow(r)),
    corpus_items: state.corpusItems.map((i) => fromCorpusItem(i)),
    ...(state.sourceRules && state.sourceRules.length > 0
      ? {
          source_rules: state.sourceRules.map((r) => ({
            id: r.id,
            rule_type: r.ruleType,
            source_type_id: r.sourceTypeId,
            display_name: r.displayName,
          })),
        }
      : {}),
    row_count: state.rowCount,
  };
}

export function toWireMessageRequest(
  request: OttoMessageRequest
): WireMessageRequest {
  return {
    message: request.content,
    ...(request.images?.length ? {images: request.images} : {}),
    surface: request.surface,
    ...(request.latticeState
      ? {lattice_state: toWireLatticeState(request.latticeState)}
      : {}),
    ...(request.latticeId ? {lattice_id: request.latticeId} : {}),
    ...(request.latticeName ? {lattice_name: request.latticeName} : {}),
    ...(request.activeSection ? {active_section: request.activeSection} : {}),
    ...(request.activeRecordId
      ? {active_record_id: request.activeRecordId}
      : {}),
    ...(request.viewDefinitionId
      ? {view_definition_id: request.viewDefinitionId}
      : {}),
    ...(request.viewScopeMode ? {view_scope_mode: request.viewScopeMode} : {}),
    ...(request.aggregateTypeId
      ? {aggregate_type_id: request.aggregateTypeId}
      : {}),
    ...(request.includeViewContent ? {include_view_content: true} : {}),
    ...(request.quoteContext
      ? {
          quote_context: {
            ...(request.quoteContext.requestId
              ? {request_id: request.quoteContext.requestId}
              : {}),
            page: request.quoteContext.page,
            view: request.quoteContext.view,
            visible_request_ids: request.quoteContext.visibleRequestIds,
            visible_quote_count: request.quoteContext.visibleQuoteCount,
            visible_intake_count: request.quoteContext.visibleIntakeCount,
            filters: request.quoteContext.filters,
            as_of: request.quoteContext.asOf,
            lane: request.quoteContext.lane,
            ...(request.quoteContext.stage
              ? {stage: request.quoteContext.stage}
              : {}),
          },
        }
      : {}),
    ...(request.procurementContext
      ? {
          procurement_context: {
            object_type_id: request.procurementContext.objectTypeId,
            object_type_name: request.procurementContext.objectTypeName,
            roots: request.procurementContext.roots,
            build_qty: request.procurementContext.buildQty,
            mode: request.procurementContext.mode,
            short_only: request.procurementContext.shortOnly,
            search: request.procurementContext.search,
            rendered_count: request.procurementContext.renderedCount,
            unknown_count: request.procurementContext.unknownCount,
            ...(request.procurementContext.lastSyncedAt
              ? {last_synced_at: request.procurementContext.lastSyncedAt}
              : {}),
          },
        }
      : {}),
    ...(request.createSession
      ? {
          create_session: true,
          // Mint-only pool selector — omitted keeps the backend's 'relay'
          // default; sent only when the caller minted a builder conversation.
          ...(request.createPageType
            ? {page_type: request.createPageType}
            : {}),
          context: {
            anchor_refs: (request.createAnchorRefs ?? []).map((a) => ({
              type_id: a.typeId,
              record_id: fromAnchorRecordId(a.recordId),
            })),
            tagged_refs: (request.createTaggedRefs ?? []).map((t) => ({
              kind: t.kind,
              id: t.id,
            })),
            ...(request.createTitle ? {title: request.createTitle} : {}),
          },
          ...(request.forkFromShareId
            ? {fork_from_share_id: request.forkFromShareId}
            : {}),
        }
      : {}),
  };
}
